import { newIdempotencyKey } from "@anthra/platform/api"
import {
	expertResponseInputSchema,
	formatIstDateTime,
	formatMobileNumber,
	formatQueryId,
	hoursBetween,
	parseTimestamp,
	QUERY_CATEGORY_OPTIONS,
	type QueryCategory,
	queryCategoryLabel,
	type ResponseDraft,
	USER_TYPE_LABELS,
	type WorkStatus,
	workStatusLabel,
} from "@anthra/platform/domain"
import { toDisplayable } from "@anthra/platform/errors"
import {
	describeTypes,
	formatBytes,
	type PlatformFile,
	QUERY_ATTACHMENT_RULES,
} from "@anthra/platform/files"
import { Breadcrumb, BreadcrumbCurrent, BreadcrumbItem } from "@anthra/ui/breadcrumb"
import { Button } from "@anthra/ui/button"
import { buttonVariants } from "@anthra/ui/button-variants"
import { cn } from "@anthra/ui/cn"
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@anthra/ui/dialog"
import { ErrorState } from "@anthra/ui/feedback"
import { NativeSelect } from "@anthra/ui/field"
import { ChevronDownIcon } from "@anthra/ui/icons"
import { ExpandableRichText } from "@anthra/ui/rich-text"
import { ScrollFab } from "@anthra/ui/scroll-fab"
import { Skeleton, SkeletonText } from "@anthra/ui/skeleton"
import { TiptapEditor } from "@anthra/ui/tiptap-editor"
import { CardTitle, Description } from "@anthra/ui/typography"
import { createFileRoute, Link } from "@tanstack/react-router"
import { useEffect, useId, useRef, useState } from "react"

import { AttachmentList } from "@/components/attachment-list"
import { FeedbackSheet } from "@/components/feedback-sheet"
import { NotFoundDenied } from "@/components/not-found-denied"
import { UserWithOwnNumber } from "@/components/user-with-own-number"
import { useMarkPanelSeen } from "@/features/activity/use-panel-activity"
import {
	COMPOSER_ANCHOR_ID,
	isDraftConflict,
	markAnsweredBlockReason,
	scrollToComposer,
} from "@/features/drafts/draft-helpers"
import {
	DraftBlockedNote,
	DraftConflictNote,
	KeptDraftFiles,
	ResponseDraftCard,
} from "@/features/drafts/response-draft"
import { type DraftComposer, useDraftComposer } from "@/features/drafts/use-draft-composer"
import { ElapsedCell } from "@/features/queries/elapsed-cell"
import {
	useCloseQuery,
	useExpertQuery,
	useMarkAnswered,
	usePostExpertResponse,
	useSaveQueryDraft,
	useUpdateQueryCategory,
} from "@/features/queries/use-expert-queries"

/**
 * One query, as an Expert works it (US-01-001.5/.6/.7/.8/.16/.19/.23/.24/.26,
 * US-01-003.1/.6).
 *
 * WHAT AN EXPERT MAY CHANGE IS THE CATEGORY, AND NOTHING ELSE. There is no
 * control here for the title, the description, the attachments or the user's
 * notes, and no delete and no export — the API refuses those writes and the
 * absence of a control is the other half of US-01-001.19.
 *
 * 'Close Query' appears ONLY on an Answered query and asks for a confirmation
 * before it fires (US-01-003.1). On anything else the endpoint answers 409 with
 * the status unchanged (US-01-003.6), so offering the button would be offering a
 * conflict.
 */
export const Route = createFileRoute("/_panel/queries/$queryId")({
	component: ExpertQueryDetail,
})

/**
 * A thread opens at its top, the question and the first messages, so the
 * conversation reads from the start (client feedback; it used to open at
 * its latest message — the composer is home.
 * Runs once the real content has painted, not the skeleton, so it scrolls to
 * the actual bottom rather than a placeholder's.
 */
function useScrollToLatestOnLoad(ready: boolean, key: string): void {
	useEffect(() => {
		if (!ready) return undefined
		// Deferred a tick: the router's own navigation-complete handler resets
		// scroll to the top AFTER this effect would otherwise run, undoing it.
		// Running one macrotask later lands after that reset.
		const timer = setTimeout(() => {
			window.scrollTo({ top: 0 })
		}, 0)
		return () => {
			clearTimeout(timer)
		}
	}, [ready, key])
}

function ExpertQueryDetail() {
	const { queryId } = Route.useParams()
	const query = useExpertQuery(queryId)
	useScrollToLatestOnLoad(query.isSuccess, queryId)
	useMarkPanelSeen("query", queryId, query.isSuccess)
	// Held here, not in the composer: the draft card in the thread loads into it.
	const composer = useDraftComposer()

	if (query.isPending) {
		return (
			<div className="flex flex-col gap-6">
				{/* THE COMMENT ABOVE THE THREAD WAS ALREADY RIGHT — "the thread's
				    own shape, not one more generic block" — and the markup under
				    it did the opposite. Every bar carried the same `h-4 w-full`,
				    including the two that were meant to be an avatar beside a
				    name and the block that was meant to be the message body, so
				    each "message" rendered as one full-width line and the three
				    were indistinguishable from the header above them. */}
				<div
					className="flex min-w-0 flex-col gap-6"
					role="status"
					aria-label="Loading the query"
					aria-busy="true"
				>
					<SkeletonText lines={2} />

					<ul className="flex flex-col gap-3">
						{[0, 1, 2].map(row => (
							<li
								className="rounded-card border-border bg-card flex flex-col gap-3 border p-3"
								key={row}
							>
								<div className="flex items-center gap-3">
									<Skeleton className="rounded-circle size-8 shrink-0" />
									<Skeleton className="h-4 w-40" />
									<Skeleton className="ms-auto h-3 w-16 shrink-0" />
								</div>
								<SkeletonText lines={2} />
							</li>
						))}
					</ul>

					{/* The composer's own footprint, so the card does not jump height
					    once the real editor mounts. */}
					<div className="border-border bg-surface flex flex-col gap-3 rounded-lg border p-5 shadow-sm">
						<Skeleton className="h-4 w-32" />
						<Skeleton className="h-24 w-full" />
						<Skeleton className="h-11 w-32" />
					</div>
				</div>
			</div>
		)
	}

	if (query.isError) {
		// A missing id is a clear not-found state with a route back, not a retry
		// loop (US-01-001.23).
		const status = (query.error as { status?: number }).status
		if (status === 404) {
			return (
				<NotFoundDenied
					markTone="info"
					title="Query not found"
					lede="That query id does not exist."
					actions={
						<Link
							className={cn(buttonVariants({ variant: "primary" }))}
							to="/"
						>
							Back to all queries
						</Link>
					}
				/>
			)
		}
		return (
			<div className="flex flex-col gap-6">
				<ErrorState
					error={query.error}
					onRetry={() => {
						void query.refetch()
					}}
				/>
			</div>
		)
	}

	const detail = query.data
	const submitted = parseTimestamp(detail.submittedAt)
	const isClosed = detail.status === "closed"

	return (
		<div className="flex flex-col gap-4">
			<ScrollFab />

			{/* A TRAIL, not a back arrow. "← Back to all queries" says where the
			    click goes but not where you are; a breadcrumb says both, and it is
			    the same component both apps use now. */}
			<Breadcrumb>
				<BreadcrumbItem>
					<Link
						to="/"
						className="hover:text-foreground underline-offset-2 hover:underline"
					>
						All queries
					</Link>
				</BreadcrumbItem>
				<BreadcrumbItem last>
					<BreadcrumbCurrent>{formatQueryId(detail.sequence)}</BreadcrumbCurrent>
				</BreadcrumbItem>
			</Breadcrumb>

			{/* A RECORD HEADER THAT READS TOP TO BOTTOM. The badges, the title
			    and the submitted line were three flex children of one wrapping
			    row set to `justify-between`, so the title was shoved into the
			    middle of the badges and the date to the far right — and at a
			    narrower width they re-ordered into a different arrangement
			    again. The title is the largest thing on the screen now and the
			    facts sit under it, which is the order somebody reads them in.

			    The <p> also carried no type role, so the submitted line
			    rendered at the browser's default size against the design's. */}
			<div className="flex flex-col gap-3">
				<div className="flex flex-col gap-1">
					<p className="text-field-label text-primary">Title</p>
					<h2 className="text-page-title text-heading min-w-0 break-all">
						{detail.title}
					</h2>
				</div>
				<dl className="flex flex-wrap gap-x-8 gap-y-3">
					<div className="flex flex-col gap-1">
						<dt className="text-meta text-muted">Status of the request</dt>
						<dd className="text-description text-foreground">
							{workStatusLabel(detail.status)}
						</dd>
					</div>
					<div className="flex flex-col gap-1">
						<dt className="text-meta text-muted">Query Category</dt>
						<dd className="text-description text-foreground">
							{queryCategoryLabel(detail.category)}
						</dd>
					</div>
					<div className="flex flex-col gap-1">
						<dt className="text-meta text-muted">Submitted at</dt>
						<dd className="text-description text-foreground tabular-nums">
							{submitted ? formatIstDateTime(submitted) : "—"}
						</dd>
					</div>
					<div className="flex flex-col gap-1">
						<dt className="text-meta text-muted">Elapsed</dt>
						<dd>
							<ElapsedCell
								submittedAt={detail.submittedAt}
								firstResponseAt={detail.firstResponseAt}
								variant="badge"
							/>
						</dd>
					</div>
				</dl>
			</div>

			{/*
			 * TWO COLUMNS ON A DESKTOP, AND THIS WRAPPER IS WHY THEY WERE NOT.
			 *
			 * It has always had exactly two children — the case (question,
			 * attachments, thread, composer) and the taxpayer rail — but it was
			 * `flex flex-col`, so on a 1440px panel the rail stacked underneath
			 * a full-width case column instead of beside it. Who the taxpayer is
			 * and how to reach them ended up a full screen below the question
			 * they asked, and the reading measure ran the width of the window.
			 *
			 * The rail is sticky because an expert scrolls the thread and the
			 * composer while still wanting the mobile number in view.
			 */}
			<div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_var(--width-rail)] lg:items-start">
				{/* `gap-1` is 4px. These are bordered cards — the question, the
				    attachments, the thread and the composer — and 4px between two
				    bordered cards reads as one card with a line through it. */}
				<div className="flex min-w-0 flex-col gap-6">
					<div className="flex flex-col gap-3">
						<h2 className="text-card-title text-foreground text-primary">
							Description
						</h2>
						{/* `description` is rich-text markup, sanitised server-side,
						    through the same renderer as the thread. There is no
						    separate plain field — a not-yet-migrated client's plain
						    text is well-formed markup with no tags, so this renders
						    it unchanged, paragraphs and all. Styled as the taxpayer
						    app's own left-accent callout rather than a bordered card,
						    so the two apps read the same question the same way. */}
						<div className="border-border bg-surface-sunken border-l-accent rounded-card border border-l-[3px] p-6">
							<ExpandableRichText
								className="max-w-[68ch]"
								html={detail.description}
							/>
						</div>
					</div>

					{detail.attachments.length > 0 ? (
						<div className="flex flex-col gap-3">
							<div className="flex flex-wrap items-center justify-between gap-2">
								<h2 className="text-card-title text-foreground text-primary">
									Attachments
								</h2>
								<span className="text-meta text-muted">
									{detail.attachments.length}{" "}
									{detail.attachments.length === 1 ? "file" : "files"}
								</span>
							</div>
							<AttachmentList
								attachments={detail.attachments}
								variant="thread"
								queryId={queryId}
							/>
						</div>
					) : null}

					<section
						className="flex min-w-0 flex-col gap-3"
						aria-label="Conversation"
					>
						<h2 className="text-card-title text-foreground text-primary">
							Conversation
						</h2>
						{detail.messages.length === 0 ? (
							<p className="text-description text-subtle-foreground">
								No messages yet.
							</p>
						) : (
							<ol className="flex min-w-0 flex-col gap-6">
								{detail.messages.map(message => {
									const isExpert = message.senderType === "expert"
									const created = parseTimestamp(message.createdAt)
									return (
										// THE EXPERT IS THE ONE READING THIS SCREEN, so their
										// own messages sit on the right and the taxpayer's on
										// the left — the side each speaker occupies in every
										// chat the Expert has ever used. The alignment read the
										// other way round because it was copied from the
										// taxpayer's app, where the sides are reversed.
										<li
											key={message.id}
											className={cn(
												"flex max-w-full gap-4",
												isExpert && "flex-row-reverse",
											)}
										>
											<div
												className={cn(
													"flex min-w-0 flex-1 flex-col gap-2",
													isExpert ? "items-end" : "items-start",
												)}
											>
												{/* Name, then the role, then the time — read
												    left to right on both sides. `flex-row-reverse`
												    aligned the expert's header to the right by
												    reversing its READING ORDER too, which put the
												    timestamp first and the name last. Justifying
												    the row moves it to the same place without
												    rewriting the sentence. */}
												<div
													className={cn(
														"flex flex-wrap items-baseline gap-x-3 gap-y-2",
														isExpert && "justify-end",
													)}
												>
													<span className="text-field-label text-foreground font-medium">
														{isExpert
															? (message.senderName ?? "Swamithra")
															: detail.user.fullName}
													</span>
													{/* No auto margin: the timestamp sits after
													    the name it belongs to rather than being
													    flung to the far edge of a half-width
													    bubble's column. */}
													<span className="text-meta text-muted tabular-nums">
														{created ? formatIstDateTime(created) : "—"}
													</span>
												</div>
												<div
													className={cn(
														// HALF THE THREAD, WHOEVER IS SPEAKING. A
														// bubble drawn edge to edge reads as the
														// page rather than as somebody's message,
														// and two different caps read as two
														// different kinds of message. This is a
														// cap, so a one-line reply still draws one
														// line wide. Matches the taxpayer app's own
														// bubble shell: rounded-lg with a shadow,
														// not the panel's flat rounded-card.
														"max-w-[70%] min-w-0 rounded-lg border px-3 py-1.5 shadow-sm",
														// The accent bubble follows the speaker,
														// not the role: it marks "your own words"
														// on whichever side those sit.
														isExpert &&
															"bg-accent-soft border-accent-border rounded-tr-xs shadow-none",
														!isExpert &&
															"bg-surface border-border rounded-tl-xs",
													)}
												>
													<ExpandableRichText
														className="text-body text-foreground"
														html={message.bodyRich}
													/>
												</div>
												{message.attachments.length > 0 ? (
													<div
														className={cn(
															"w-full min-w-0",
															isExpert && "flex justify-end",
														)}
													>
														<AttachmentList
															attachments={message.attachments}
															variant="thread"
															queryId={queryId}
														/>
													</div>
												) : null}
											</div>
										</li>
									)
								})}
							</ol>
						)}
						{/* Once Edit has moved the draft into the reply box, it is no
						    longer shown here: the text and files live in one place. */}
						{detail.responseDraft && !isClosed && composer.loaded === null ? (
							<ResponseDraftCard
								draft={detail.responseDraft}
								onEdit={() => {
									if (!detail.responseDraft) return
									composer.load(detail.responseDraft)
									scrollToComposer()
								}}
							/>
						) : null}
					</section>

					{/* User Feedback Section - Displayed when feedback is submitted */}
					<FeedbackSheet
						feedback={detail.feedback}
						userName={detail.user.fullName}
					/>

					{/* On a Closed query the editor and the status actions are hidden and
					    the thread is read-only (US-01-001.16). */}
					{isClosed ? (
						<div className="rounded-card border-status-open-border bg-status-open-bg flex items-start gap-3 border p-3">
							<div className="flex min-w-0 flex-col gap-1">
								<p className="text-card-title text-foreground">
									This query is closed
								</p>
								<p>
									{detail.closedAt
										? formatIstDateTime(
												parseTimestamp(detail.closedAt) ?? new Date(),
											)
										: ""}{" "}
									· read-only, including the follow-up box.
								</p>
							</div>
						</div>
					) : (
						<ResponseArea
							composer={composer}
							draft={detail.responseDraft}
							hasExpertReply={detail.messages.some(
								message => message.senderType === "expert",
							)}
							queryId={detail.id}
							status={detail.status}
							submittedAt={detail.submittedAt}
							firstResponseAt={detail.firstResponseAt}
							userFullName={detail.user.fullName}
						/>
					)}
				</div>

				<div className="flex flex-col gap-4 lg:sticky lg:top-6">
					<article className="border-border bg-surface flex flex-col gap-3 rounded-lg border p-5 shadow-sm">
						<div className="flex flex-wrap items-center justify-between gap-2">
							<p className="text-overline text-muted uppercase">User</p>
						</div>
						{/* The avatar sits BESIDE the name, not above it: this row
						    was `flex-col`, which stacked a 36px circle on top of
						    the name it belongs to and made the card read as two
						    unrelated things. */}
						<div className="flex min-w-0 items-center gap-3">
							<span
								className="bg-avatar-bg text-avatar-text rounded-circle flex size-9 shrink-0 items-center justify-center font-semibold"
								aria-hidden="true"
							>
								{detail.user.fullName.slice(0, 2).toUpperCase()}
							</span>
							<span className="min-w-0">
								<span className="text-field-label text-foreground font-medium">
									<UserWithOwnNumber
										name={detail.user.fullName}
										userReference={detail.userReference}
									/>
								</span>
								<br />
								<span className="text-meta text-muted">
									{USER_TYPE_LABELS[detail.user.userType]}
								</span>
							</span>
						</div>
						<hr className="text-meta text-muted" />
						<dl className="flex flex-col gap-2">
							<div>
								<dt>Mobile</dt>
								{/* In full, and ONLY here — never in the dashboard list
								    (US-01-001.5/.21). */}
								<dd>
									<a
										/* `tabular-nums`, not `font-mono`. The
										   number is already formatted with
										   spaces, and a monospace face widens
										   every one of them — "+91  98450
										   12345" with double gaps. Lining
										   figures give the alignment the mono
										   was there for without the spacing. */
										className="tabular-nums"
										href={`tel:${detail.user.mobileNumber}`}
									>
										{formatMobileNumber(detail.user.mobileNumber)}
									</a>
								</dd>
							</div>
							<div>
								<dt>Submitted</dt>
								<dd>{submitted ? formatIstDateTime(submitted) : "—"}</dd>
							</div>
						</dl>
					</article>

					<CategoryControl
						queryId={detail.id}
						category={detail.category}
						disabled={isClosed}
					/>
				</div>
			</div>
		</div>
	)
}

/**
 * Category correction (US-01-001.8, US-01-006.10).
 *
 * The one user-supplied field an Expert may change. The write is audited
 * server-side with the previous and new value (US-01-001.20), and the reporting
 * figures follow the CURRENT category, which the mutation handles by dropping the
 * reporting cache.
 */
function CategoryControl({
	queryId,
	category,
	disabled,
}: {
	queryId: string
	category: QueryCategory
	disabled: boolean
}) {
	const update = useUpdateQueryCategory()
	const selectId = useId()

	return (
		<section className="border-border bg-surface flex flex-col gap-4 rounded-lg border p-5 shadow-sm">
			<div className="flex flex-wrap items-center justify-between gap-2">
				<h2 className="text-card-title text-foreground">Query Category</h2>
			</div>
			{/* `isSuccess` clears itself the moment another `mutate()` starts, so
			    this needs no dismiss of its own — picking a different category
			    replaces it with either the next confirmation or an error. */}
			{update.isSuccess ? (
				<div
					className="rounded-card border-status-answered-border bg-status-answered-bg flex items-start gap-3 border p-3"
					role="status"
				>
					<div className="flex min-w-0 flex-col gap-1">
						<span className="text-card-title text-foreground">Category corrected</span>
						<p>
							Now counted under {queryCategoryLabel(update.variables.category)} in
							reporting and under that filter in the queue.
						</p>
					</div>
				</div>
			) : null}
			{/* Hand-rolled rather than the shared `Field`: `Field` draws its own
			    label and hint in the shared kit's Tailwind classes, and this
			    screen's own `.label` (screens/expert-query-detail.html) never
			    rendered underneath it. */}
			{/* `max-w-sm`: seven options of one or two words each do not need a
			    1000px control. A field stretched to the width of its card is the
			    clearest sign of a form that was never laid out. */}
			<div className="flex max-w-sm min-w-56 flex-col gap-1.5">
				{/* Named by the card's heading above; the label stays for screen
				    readers only, so "Query Category" is shown once. */}
				<label
					className="sr-only"
					htmlFor={selectId}
				>
					Query Category
				</label>
				<span className="relative">
					<NativeSelect
						id={selectId}
						name="category"
						value={category}
						disabled={disabled || update.isPending}
						aria-invalid={update.isError}
						onChange={event => {
							if (disabled || update.isPending) return
							update.mutate({
								queryId,
								category: event.target.value as QueryCategory,
							})
						}}
					>
						{QUERY_CATEGORY_OPTIONS.map(option => (
							<option
								key={option.value}
								value={option.value}
							>
								{option.label}
							</option>
						))}
					</NativeSelect>
					{/* THE ICON SET, not the "▾" character. Set in a `size-4`
					    box the glyph sat off its own baseline and rendered as a
					    small clipped tick against the right edge of the field —
					    and it is drawn by whichever font on the machine happens
					    to carry U+25BE, so it did not match the chevron on the
					    Radix Select two screens away. */}
					<ChevronDownIcon
						className="text-muted pointer-events-none absolute top-1/2 right-3 size-4 -translate-y-1/2"
						aria-hidden="true"
					/>
				</span>
				<p className="text-meta text-muted">
					{disabled
						? "Category cannot be changed on a closed query."
						: "Correcting a mis-tagged query is recorded in the audit log."}
				</p>
				{update.isError ? (
					<p
						className="text-description text-subtle-foreground"
						role="alert"
					>
						{toDisplayable(update.error).message}
					</p>
				) : null}
			</div>
		</section>
	)
}

/**
 * The response area (US-01-001.6/.7/.15/.24/.26, US-01-003.1).
 *
 * A failed send keeps the composed markup and attachments in the editor, and a
 * retry reuses the same idempotency key, so it posts exactly one message and
 * sends one notification (US-01-001.24).
 */
function ResponseArea({
	composer,
	draft,
	hasExpertReply,
	queryId,
	status,
	submittedAt,
	firstResponseAt,
	userFullName,
}: {
	composer: DraftComposer
	/** The saved response draft on this query, or null. */
	draft: ResponseDraft | null
	/** At least one Expert reply is in the thread. */
	hasExpertReply: boolean
	queryId: string
	/** Never `closed` — the caller hides this whole area on a Closed query. */
	status: WorkStatus
	submittedAt: string
	firstResponseAt: string | null
	userFullName: string
}) {
	const post = usePostExpertResponse(() => ({
		is_first_response: firstResponseAt === null,
		hours_since_submitted: Math.max(0, hoursSinceSubmitted()),
	}))
	const markAnswered = useMarkAnswered(() => ({
		hours_since_submitted: Math.max(0, hoursSinceSubmitted()),
	}))
	const close = useCloseQuery()
	const save = useSaveQueryDraft()
	// A saved draft this Expert has not opened locks the composer: a second
	// reply cannot be started beside it. Edit it first.
	const blocked = draft !== null && composer.loaded === null
	// Mark as Answered waits for at least one reply, and for a saved draft that
	// nobody has opened. Once Edit has loaded the draft here, it no longer blocks.
	const answerBlocked = markAnsweredBlockReason(blocked, hasExpertReply)

	const { bodyRich, files, rejections } = composer
	const [error, setError] = useState<string | undefined>(undefined)
	const sendKey = useRef<string | null>(null)
	const actionKey = useRef<string | null>(null)
	const inputRef = useRef<HTMLInputElement>(null)

	/**
	 * Save draft. `over` is set when the Expert chose to save over a newer draft
	 * another Expert saved; see DraftConflictNote.
	 */
	function saveDraft(over?: ResponseDraft | null): void {
		setError(undefined)
		save.mutate(
			{ queryId, input: composer.saveInput(over), attachments: files },
			{
				onSuccess: () => {
					composer.clear()
				},
			},
		)
	}

	function loadLatest(): void {
		if (!draft) return
		composer.load(draft)
		save.reset()
		post.reset()
	}

	function hoursSinceSubmitted(): number {
		const submitted = parseTimestamp(submittedAt)
		return submitted ? hoursBetween(submitted, new Date()) : 0
	}

	function send(): void {
		const parsed = expertResponseInputSchema.safeParse({ bodyRich })
		if (!parsed.success) {
			setError(parsed.error.issues[0]?.message)
			return
		}
		setError(undefined)
		sendKey.current ??= newIdempotencyKey()

		post.mutate(
			{
				queryId,
				input: parsed.data,
				attachments: files,
				idempotencyKey: sendKey.current,
				fromDraft: composer.sendFromDraft(),
			},
			{
				onSuccess: () => {
					composer.clear()
					sendKey.current = null
				},
				onError: failure => {
					// A conflict changes what the retry will send (the Expert loads
					// the newer draft first), so it must not reuse this key.
					if (isDraftConflict(failure)) sendKey.current = null
				},
			},
		)
	}

	return (
		<details
			id={COMPOSER_ANCHOR_ID}
			className="group border-border bg-surface flex scroll-mt-6 flex-col gap-3 rounded-lg border p-5 shadow-sm"
		>
			{/* AN ACCORDION, CLOSED UNTIL ASKED FOR, like the user app's follow-up
			    box. The composer stays mounted while closed, so a half-written
			    reply is not lost by folding it away; Edit on a saved draft opens
			    it (scrollToComposer). */}
			<summary className="flex cursor-pointer list-none items-center justify-between gap-3 [&::-webkit-details-marker]:hidden">
				<h2 className="text-card-title text-foreground text-primary">Your response</h2>
				<svg
					className="text-muted size-5 shrink-0 transition-transform group-open:rotate-180"
					viewBox="0 0 24 24"
					fill="none"
					stroke="currentColor"
					strokeWidth="2"
					strokeLinecap="round"
					strokeLinejoin="round"
					aria-hidden="true"
				>
					<path d="m6 9 6 6 6-6" />
				</svg>
			</summary>
			<div className="mt-2 flex flex-col gap-3">
				<p className="text-meta text-muted">
					Posts to {userFullName}&apos;s thread as an expert reply
				</p>

				{/* Each confirmation clears itself the moment the Expert starts the
			    next mutation — sending again, or marking answered — since
			    `mutate()` resets `isSuccess` at the start of a new attempt. */}
				{markAnswered.isSuccess ? (
					<div
						className="rounded-card border-status-answered-border bg-status-answered-bg flex items-start gap-3 border p-3"
						role="status"
					>
						<div className="flex min-w-0 flex-col gap-1">
							<span className="text-card-title text-foreground">
								Query marked as answered
							</span>
							<p>
								The user has been notified on WhatsApp with the query number and a
								link. They can now say whether this was helpful, which closes the
								query — you do not need to do anything else.
							</p>
						</div>
					</div>
				) : post.isSuccess ? (
					<div
						className="rounded-card border-status-answered-border bg-status-answered-bg flex items-start gap-3 border p-3"
						role="status"
					>
						<div className="flex min-w-0 flex-col gap-1">
							<span className="text-card-title text-foreground">Response sent</span>
							<p>
								Posted to the user&apos;s thread and they have been notified on
								WhatsApp.
							</p>
						</div>
					</div>
				) : save.isSuccess && blocked ? (
					<div
						className="rounded-card border-status-answered-border bg-status-answered-bg flex items-start gap-3 border p-3"
						role="status"
					>
						<div className="flex min-w-0 flex-col gap-1">
							<span className="text-card-title text-foreground">Draft saved</span>
							<p>It is in the conversation above. Use Edit on it to carry on.</p>
						</div>
					</div>
				) : null}

				{blocked && !save.isSuccess ? <DraftBlockedNote draft={draft} /> : null}

				<TiptapEditor
					label="Your response"
					value={bodyRich}
					onChange={composer.setBodyRich}
					disabled={blocked}
					error={error}
				/>

				<input
					ref={inputRef}
					type="file"
					className="sr-only"
					multiple
					accept={QUERY_ATTACHMENT_RULES.accepted.join(",")}
					aria-label="Attach files"
					onChange={event => {
						const chosen: PlatformFile[] = Array.from(event.target.files ?? []).map(
							file => ({
								name: file.name,
								type: file.type,
								sizeBytes: file.size,
								source: file,
							}),
						)
						if (chosen.length > 0) composer.addFiles(chosen)
						event.target.value = ""
					}}
				/>

				<div className="mt-4 flex flex-wrap items-center justify-between gap-3">
					<div className="flex flex-row items-center gap-3">
						<button
							type="button"
							className={cn(buttonVariants({ variant: "secondary" }))}
							disabled={blocked}
							onClick={() => inputRef.current?.click()}
						>
							Attach files
						</button>
						<span className="text-meta text-muted tabular-nums">
							{describeTypes(QUERY_ATTACHMENT_RULES.accepted)} · up to{" "}
							{formatBytes(QUERY_ATTACHMENT_RULES.maxBytes)} ·{" "}
							{QUERY_ATTACHMENT_RULES.maxFiles} files maximum
						</span>
					</div>
					<div className="flex flex-row items-center gap-3">
						<Button
							variant="secondary"
							disabled={blocked || save.isPending || post.isPending}
							onClick={() => {
								saveDraft()
							}}
						>
							{save.isPending ? "Saving…" : "Save draft"}
						</Button>
						<Button
							variant="primary"
							disabled={blocked || post.isPending || save.isPending}
							onClick={send}
						>
							{post.isPending ? "Sending…" : "Send Response"}
						</Button>
					</div>
				</div>

				<KeptDraftFiles
					files={composer.loaded?.kept ?? []}
					onRemove={composer.removeKept}
				/>

				{files.length > 0 ? (
					<ul className="flex flex-col gap-2">
						{files.map((file, index) => (
							<li
								key={`${file.name}-${String(index)}`}
								className="border-border-subtle bg-surface-sunken flex items-center gap-3 rounded-md border px-3 py-2"
							>
								<span className="text-description text-foreground min-w-0 flex-1 truncate">
									{file.name}
								</span>
								<span className="text-meta text-muted shrink-0 tabular-nums">
									{formatBytes(file.sizeBytes)}
								</span>
								<Button
									type="button"
									variant="quiet"
									size="icon"
									aria-label={`Remove ${file.name}`}
									onClick={() => {
										composer.removeFile(index)
									}}
								>
									<svg
										viewBox="0 0 24 24"
										fill="none"
										stroke="currentColor"
										strokeWidth="1.7"
										strokeLinecap="round"
										strokeLinejoin="round"
										aria-hidden="true"
									>
										<path d="M6 6l12 12M18 6 6 18" />
									</svg>
								</Button>
							</li>
						))}
					</ul>
				) : null}

				{rejections.length > 0 ? (
					<ul className="flex flex-col gap-2">
						{rejections.map(rejection => (
							<li
								key={rejection.message}
								className="border-danger-border bg-danger-soft flex items-center gap-2 rounded-md border px-3 py-2"
								role="alert"
							>
								<span className="text-description text-danger min-w-0 flex-1 truncate">
									{rejection.message}
								</span>
							</li>
						))}
					</ul>
				) : null}

				{save.isError && isDraftConflict(save.error) ? (
					<DraftConflictNote
						error={save.error}
						latest={draft}
						onLoadLatest={loadLatest}
						onSaveOver={() => {
							saveDraft(draft)
						}}
					/>
				) : null}
				{save.isError && !isDraftConflict(save.error) ? (
					<ErrorState
						error={save.error}
						onRetry={() => {
							saveDraft()
						}}
					/>
				) : null}
				{post.isError && isDraftConflict(post.error) ? (
					<DraftConflictNote
						error={post.error}
						latest={draft}
						onLoadLatest={loadLatest}
					/>
				) : null}
				{post.isError && !isDraftConflict(post.error) ? (
					<div className="rounded-card border-status-breach-border bg-status-breach-bg flex items-start gap-3 border p-3">
						<div className="flex min-w-0 flex-col gap-1">
							<ErrorState
								error={post.error}
								onRetry={send}
							/>
						</div>
					</div>
				) : null}
				{markAnswered.isError ? (
					<div className="rounded-card border-status-breach-border bg-status-breach-bg flex items-start gap-3 border p-3">
						<div className="flex min-w-0 flex-col gap-1">
							<ErrorState error={markAnswered.error} />
						</div>
					</div>
				) : null}
				{close.isError ? (
					<div className="rounded-card border-status-breach-border bg-status-breach-bg flex items-start gap-3 border p-3">
						<div className="flex min-w-0 flex-col gap-1">
							<ErrorState error={close.error} />
						</div>
					</div>
				) : null}

				<div className="border-border flex flex-wrap items-center justify-between gap-2 pt-3">
					{status === "in_progress" ? (
						<div className="flex flex-wrap items-center gap-3">
							<Button
								variant="secondary"
								disabled={markAnswered.isPending || answerBlocked !== null}
								onClick={() => {
									actionKey.current ??= newIdempotencyKey()
									markAnswered.mutate(
										{ queryId, idempotencyKey: actionKey.current },
										{
											onSuccess: () => {
												actionKey.current = null
											},
										},
									)
								}}
							>
								Mark as Answered
							</Button>
							{answerBlocked ? (
								<span className="text-meta text-muted">{answerBlocked}</span>
							) : null}
						</div>
					) : null}

					{status === "answered" ? (
						<CloseQueryAction
							isPending={close.isPending}
							onConfirm={() => {
								actionKey.current ??= newIdempotencyKey()
								close.mutate(
									{ queryId, idempotencyKey: actionKey.current },
									{
										onSuccess: () => {
											// No WhatsApp message is sent on a close, by
											// any of the three routes (US-01-003.11).
											actionKey.current = null
										},
									},
								)
							}}
						/>
					) : null}
				</div>
			</div>
		</details>
	)
}

/**
 * 'Close Query', and the confirmation it goes through (US-01-003.1).
 *
 * THE SECOND STEP IS THE REQUIREMENT, not a nicety: the criterion is written as
 * "clicks 'Close Query' and confirms", and closing is one-way — nothing reopens a
 * Closed query, feedback on it is refused with 409, and the user loses the
 * follow-up box (US-01-003.8/.9). A single misplaced click ending a conversation
 * the user was still in is not a state any screen here can undo.
 *
 * The dialog says what closing does rather than asking 'Are you sure?', which
 * tells the Expert nothing they did not already know.
 */
function CloseQueryAction({ isPending, onConfirm }: { isPending: boolean; onConfirm: () => void }) {
	const [open, setOpen] = useState(false)

	return (
		<>
			<Button
				variant="secondary"
				disabled={isPending}
				onClick={() => {
					setOpen(true)
				}}
			>
				Close Query
			</Button>

			<Dialog
				open={open}
				onOpenChange={setOpen}
			>
				<DialogContent>
					<DialogHeader>
						<DialogTitle asChild>
							<CardTitle as="h2">Close this query?</CardTitle>
						</DialogTitle>
						<DialogDescription asChild>
							<Description>
								The user keeps the thread but loses the follow-up box. Closing
								cannot be undone, and they are not notified.
							</Description>
						</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<Button
							variant="secondary"
							onClick={() => {
								setOpen(false)
							}}
						>
							Cancel
						</Button>
						<Button
							disabled={isPending}
							onClick={() => {
								setOpen(false)
								onConfirm()
							}}
						>
							{isPending ? "Closing…" : "Yes, close it"}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</>
	)
}
